import type { StateView } from '@quiz/shared';
import { Card, Leaderboard } from '../components/ui.js';

export function RevealScreen({ state, playerId }: { state: StateView; playerId: string }) {
  const reveal = state.reveal;
  if (!reveal) return null;

  const tone = reveal.youTimedOut
    ? { bg: 'bg-ink-3', text: 'text-muted', label: 'No answer' }
    : reveal.youWereCorrect
      ? { bg: 'bg-go/15', text: 'text-go', label: 'Correct' }
      : { bg: 'bg-stop/15', text: 'text-stop', label: 'Not this time' };

  const totalTally = Object.values(reveal.optionTally ?? {}).reduce((a, b) => a + b, 0);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="text-sm text-muted">
        {state.round?.title} &middot; Question {(state.question?.indexInRound ?? 0) + 1} of{' '}
        {state.question?.totalInRound}
      </div>

      <Card className={tone.bg}>
        <p className={`text-3xl font-bold ${tone.text}`}>{tone.label}</p>
        {!reveal.youTimedOut && (
          <p className="mt-1 text-sm text-muted">
            You said &ldquo;{reveal.yourAnswer}&rdquo;
          </p>
        )}
        <p className="mt-4 text-xs uppercase tracking-wide text-muted">The answer</p>
        <p className="text-2xl font-semibold">{reveal.correctAnswer}</p>
        {reveal.explanation && <p className="mt-3 text-sm text-muted">{reveal.explanation}</p>}
        {reveal.pointsEarned > 0 && (
          <p className="mt-4 text-xl font-bold text-flag">+{reveal.pointsEarned} points</p>
        )}
      </Card>

      {reveal.optionTally && totalTally > 0 && (
        <Card>
          <h2 className="mb-3 text-xs uppercase tracking-wide text-muted">What the room said</h2>
          <div className="space-y-2">
            {Object.entries(reveal.optionTally)
              .sort(([, a], [, b]) => b - a)
              .map(([option, count]) => (
                <div key={option}>
                  <div className="flex justify-between text-sm">
                    <span className={option === reveal.correctAnswer ? 'text-go' : ''}>
                      {option}
                    </span>
                    <span className="tabular-nums text-muted">{count}</span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-3">
                    <div
                      className={`h-full ${option === reveal.correctAnswer ? 'bg-go' : 'bg-ink-3'}`}
                      style={{ width: `${(count / totalTally) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
          </div>
        </Card>
      )}

      {state.leaderboard && (
        <Leaderboard rows={state.leaderboard} highlightPlayerId={playerId} title="Top ten" />
      )}
    </div>
  );
}
